import type { SessionService } from '@efdigital/ll-addon-auth';
import type { MediaImage } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import {
  buildGetAiTagsRequest,
  buildGetSignedUploadUrlRequest,
  createMediaImage,
  parseGetAiTagsResponse,
  parseGetSignedUploadUrlResponse,
} from '@efdigital/ll-admin-v2/data/builders/content/media-images';
import type MediaImagesService from '@efdigital/ll-admin-v2/services/media-images';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { cell, resource, use } from 'ember-resources';

interface Signature {
  Args: {
    selectedFile: File;
    onUploadComplete?: (image: MediaImage) => void;
  };
}

const MIN_WIDTH = 800;
const MIN_HEIGHT = 600;

function not(value: unknown): boolean {
  return !value;
}

export class ImageUploaderFileUpload extends Component<Signature> {
  @service declare store: Store;
  @service declare session: SessionService;
  @service('media-images') declare mediaImages: MediaImagesService;

  @tracked title = '';
  @tracked alt = '';
  @tracked isUploading = false;
  @tracked uploadProgress = 0;
  @tracked uploadError: string | null = null;

  @use previewUrl = resource(() => {
    const result = cell<string>('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      result.set(e.target?.result as string);
    };
    reader.readAsDataURL(this.args.selectedFile);

    return () => result.current;
  });

  @use imageSize = resource(() => {
    const result = cell<{ width: number; height: number } | null>(null);

    if (this.previewUrl) {
      const img = new Image();
      img.onload = () => {
        result.set({ width: img.width, height: img.height });
      };
      img.src = this.previewUrl;
    } else {
      result.set(null);
    }

    return () => result.current;
  });

  get imageTooSmall() {
    const size = this.imageSize;
    if (!size) {
      return false;
    }

    return size.width < MIN_WIDTH || size.height < MIN_HEIGHT;
  }

  updateTitle = (event: Event): void => {
    this.title = (event.target as HTMLInputElement).value;
  };

  updateAlt = (event: Event): void => {
    this.alt = (event.target as HTMLInputElement).value;
  };

  get previewImageStyle() {
    return htmlSafe('max-height: 400px;');
  }

  get progressBarStyle() {
    return htmlSafe(`width: ${this.uploadProgress}%`);
  }

  get canUpload(): boolean {
    return this.title.trim() !== '' && !this.isUploading;
  }

  upload = async (): Promise<void> => {
    if (!this.title) {
      return;
    }

    this.isUploading = true;
    this.uploadError = null;
    this.uploadProgress = 0;

    try {
      // Step 1: Create the media-image record via JSON:API POST
      const mediaImage = this.store.createRecord<MediaImage>(
        DataTypeContent.MediaImage,
        {
          title: this.title,
          alt: this.alt || this.title,
        }
      );
      const createRecordRequest = createMediaImage(mediaImage, this.store);
      const createMediaImageResponse =
        await this.store.request<ReactiveDataDocument<MediaImage>>(
          createRecordRequest
        );
      const imageId = createMediaImageResponse.content.data.id;
      if (!imageId) {
        throw new Error('Image ID is required');
      }

      this.uploadProgress = 20;

      // Step 2: Get signed upload URL from management API
      const request = await buildGetSignedUploadUrlRequest(
        imageId,
        this.args.selectedFile.type,
        this.session
      );
      const signedUrlResponse = await fetch(request);
      if (!signedUrlResponse.ok) {
        throw new Error(
          `Failed to get upload URL: ${signedUrlResponse.statusText}`
        );
      }

      const { url } = await parseGetSignedUploadUrlResponse(signedUrlResponse);
      this.uploadProgress = 40;

      // Step 3: Upload file to signed S3 URL
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': this.args.selectedFile.type,
        },
        body: this.args.selectedFile,
      });

      if (!uploadResponse.ok) {
        throw new Error(`File upload failed: ${uploadResponse.statusText}`);
      }

      this.uploadProgress = 50;

      // Wait for server to process and load the image to get thumbnailUrl
      const newImage = await this.mediaImages.waitForThumbnail(imageId);
      this.uploadProgress = 75;

      try {
        const taggingResponse = await fetch(
          await buildGetAiTagsRequest(
            newImage.thumbnailUrl,
            this.mediaImages.session
          )
        );
        const data = await parseGetAiTagsResponse(taggingResponse);
        if (data.tags && data.tags.length > 0) {
          // Filter out tags that are already in keywords
          newImage.keywords = data.tags;
        }
      } catch (error) {
        console.warn('Failed to fetch AI tags:', error);
      }
      this.uploadProgress = 100;

      this.args.onUploadComplete?.(newImage);
    } catch (error) {
      this.uploadError =
        error instanceof Error ? error.message : 'Upload failed';
    } finally {
      this.isUploading = false;
    }
  };

  <template>
    {{! Preview and metadata form }}
    <div class="upload-form">
      {{#if this.previewUrl}}
        <div class="text-center mb-4">
          <img
            src={{this.previewUrl}}
            alt="Preview"
            class="img-fluid rounded shadow-sm"
            style={{this.previewImageStyle}}
          />
        </div>
      {{/if}}

      {{! Validation messages }}
      {{#if this.imageSize}}
        {{#if this.imageTooSmall}}
          <div class="mb-3">
            <div class="alert alert-warning">
              Image dimensions ({{this.imageSize.width}}×{{this.imageSize.height}})
              are below recommended minimum ({{MIN_WIDTH}}×{{MIN_HEIGHT}}).
              Quality may be reduced.
            </div>
          </div>
        {{/if}}
      {{/if}}

      {{! Title input }}
      <div class="mb-3">
        <label for="image-title" class="form-label">Title *</label>
        <input
          type="text"
          id="image-title"
          class="form-control"
          value={{this.title}}
          {{on "input" this.updateTitle}}
          placeholder="Enter image title"
          required
        />
      </div>

      {{! Alt text input }}
      <div class="mb-3">
        <label for="image-alt" class="form-label">Alt Text</label>
        <input
          type="text"
          id="image-alt"
          class="form-control"
          value={{this.alt}}
          {{on "input" this.updateAlt}}
          placeholder="Describe the image for accessibility"
        />
        <small class="form-text text-muted">
          If empty, title will be used. Describe the image for screen readers.
        </small>
      </div>

      {{! Upload progress }}
      {{#if this.isUploading}}
        <div class="mb-3">
          <div class="progress">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{this.progressBarStyle}}
              aria-valuenow={{this.uploadProgress}}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{this.uploadProgress}}%
            </div>
          </div>
        </div>
      {{/if}}

      {{! Error message }}
      {{#if this.uploadError}}
        <div class="alert alert-danger">
          {{this.uploadError}}
        </div>
      {{/if}}

      <button
        type="button"
        class="btn btn-primary"
        {{on "click" this.upload}}
        disabled={{not this.canUpload}}
      >
        {{#if this.isUploading}}
          <span class="spinner-border spinner-border-sm me-2"></span>
          Uploading...
        {{else}}
          Upload Image
        {{/if}}
      </button>
    </div>
  </template>
}
